import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Download } from 'lucide-react';
import { orderService } from '../../services/orderService';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import Input from '../../components/common/Input';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';
import { formatOrderNumber } from '../../utils/statusBucket';
import { formatOrderStatus } from '../../utils/enums';
import { exportCsv } from '../../utils/exportCsv';

const PAGE_SIZE = 20;

const OrdersManagement = () => {
  const navigate = useNavigate();
  const { currentStore } = useAuth();
  const isResellerStore = currentStore?.businessType === 'reseller';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    statusBucket: '', q: '', from: '', to: ''
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderService.getAll({
        statusBucket: filter.statusBucket || undefined,
        q: filter.q || undefined,
        from: filter.from || undefined,
        to: filter.to || undefined,
        page,
        limit: PAGE_SIZE
      });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const baseColumns = [
    {
      key: 'number',
      header: 'Pedido',
      width: 110,
      render: (o) => <span className="font-mono text-sm">{formatOrderNumber(o)}</span>,
    },
    {
      key: 'customer',
      header: 'Cliente',
      render: (o) => (
        <div>
          <p className="font-medium text-app-text">{o.customer?.name || '—'}</p>
          <p className="text-xs text-app-text-secondary">{o.customer?.email || ''}</p>
        </div>
      ),
    }
  ];

  const resellerColumn = isResellerStore ? [{
    key: 'reseller',
    header: 'Revendedor',
    render: (o) => o.reseller ? (
      <div>
        <p className="font-medium text-app-text">{o.reseller.name}</p>
        <p className="text-xs text-app-text-secondary">{o.reseller.email}</p>
      </div>
    ) : (
      <span className="text-xs text-app-text-secondary">— Direto —</span>
    )
  }] : [];

  const columns = [
    ...baseColumns,
    ...resellerColumn,
    {
      key: 'createdAt',
      header: 'Data',
      render: (o) => (
        <span className="text-sm text-app-text-secondary">{formatDateTime(o.createdAt)}</span>
      ),
    },
    {
      key: 'totalToPay',
      header: 'Total',
      align: 'right',
      render: (o) => <span className="font-semibold">{formatPrice(o.totalToPay)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (o) => <StatusBadge domain="order" status={o.statusBucket || o.status} />,
    },
  ];

  const clearFilters = () => {
    setFilter({ statusBucket: '', q: '', from: '', to: '' });
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Pedidos</h1>
        <Button
          variant="subtle"
          leftIcon={<Download size={16} />}
          onClick={() =>
            exportCsv({
              filename: 'pedidos.csv',
              columns: [
                { key: 'sequentialNumber', header: 'Número' },
                { header: 'Cliente', format: (_, r) => r.customer?.name || '' },
                { header: 'Email', format: (_, r) => r.customer?.email || '' },
                ...(isResellerStore ? [{ header: 'Revendedor', format: (_, r) => r.reseller?.name || '' }] : []),
                { key: 'createdAt', header: 'Data', format: formatDateTime },
                { key: 'subtotal', header: 'Subtotal' },
                { key: 'totalToPay', header: 'Total a pagar' },
                { key: 'totalDiscount', header: 'Desconto revendedor' },
                { key: 'statusBucket', header: 'Status', format: (v) => formatOrderStatus(v) || '—' },
                { key: 'status', header: 'Status detalhado', format: (v) => formatOrderStatus(v) || '—' },
              ],
              rows,
            })
          }
          disabled={!rows.length}
        >
          Exportar CSV
        </Button>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <SearchInput
              value={filter.q}
              onChange={(q) => { setFilter((f) => ({ ...f, q })); setPage(1); }}
              placeholder="Buscar por número, cliente ou email…"
              debounceMs={300}
            />
          </div>
          <Select
            value={filter.statusBucket}
            onChange={(e) => { setFilter((f) => ({ ...f, statusBucket: e.target.value })); setPage(1); }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'PENDING', label: 'Pendentes' },
              { value: 'PAID', label: 'Pagos' },
              { value: 'CANCELED', label: 'Cancelados' },
              { value: 'REFUNDED', label: 'Reembolsados' },
            ]}
          />
          <Input
            type="date"
            value={filter.from}
            onChange={(e) => { setFilter((f) => ({ ...f, from: e.target.value })); setPage(1); }}
            placeholder="De"
          />
          <Input
            type="date"
            value={filter.to}
            onChange={(e) => { setFilter((f) => ({ ...f, to: e.target.value })); setPage(1); }}
            placeholder="Até"
          />
        </div>
        {(filter.q || filter.statusBucket || filter.from || filter.to) && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={clearFilters}>Limpar filtros</Button>
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onRowClick={(o) => navigate(`/fornecedor/painel/pedidos/${o.id}`)}
        empty={
          <EmptyState
            icon={ShoppingBag}
            title={filter.q || filter.statusBucket ? 'Nenhum pedido bate com os filtros' : 'Sem pedidos ainda'}
            description={filter.q || filter.statusBucket ? 'Ajuste os filtros.' : 'Assim que algum cliente comprar, aparece aqui.'}
          />
        }
      />
    </div>
  );
};

export default OrdersManagement;
